import React, { useState } from 'react'
import { useQuery, useMutation } from 'urql'
import { GET_COMPANIES, UPDATE_COMPANY_STATUS } from '../../graphql/companies'

function StatusBadge({ status }) {
  const statusMap = {
    NORMAL: { class: 'normal', label: 'Activa' },
    ROJO: { class: 'rojo', label: 'Pago Pendiente' },
    BLOQUEADO: { class: 'bloqueado', label: 'Bloqueada' },
  }

  const statusInfo = statusMap[status] || statusMap.NORMAL

  return (
    <span className={`status-badge ${statusInfo.class}`}>
      {statusInfo.label}
    </span>
  )
}

function ServiceBadge({ plan }) {
  if (!plan) return <span className="text-muted">-</span>
  
  const planName = plan === 'servicio1' ? 'SERVICIO 1' 
    : plan === 'servicio2' ? 'SERVICIO 2' 
    : plan === 'servicio3' ? 'SERVICIO 3' 
    : plan.toUpperCase()
  
  return <span className="service-badge">{planName}</span>
}

function StatusModal({ company, onClose, onConfirm, isBulk = false, selectedCount = 0 }) {
  const [newStatus, setNewStatus] = useState(company?.adminStatus || 'NORMAL')

  const handleSubmit = (e) => {
    e.preventDefault()
    onConfirm(isBulk ? null : company?._id, newStatus)
  }

  const modalTitle = isBulk 
    ? `Cambiar Estado - ${selectedCount} empresas seleccionadas`
    : `Cambiar Estado - ${company?.name}`

  const submitButtonText = isBulk ? `Aplicar a ${selectedCount} empresas` : 'Guardar'

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">{modalTitle}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Nuevo Estado</label>
              <select
                className="form-select"
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
              >
                <option value="NORMAL">Normal (Activa)</option>
                <option value="ROJO">Rojo (Falta de Pago)</option>
                <option value="BLOQUEADO">Bloqueado (Deja de ser cliente)</option>
              </select>
            </div>
            {/* Campo 'Razón del cambio' eliminado por requerimiento */}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary">
              {submitButtonText}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export function CompaniesList({ onSelectCompany, embedded = false }) {
  const [filters, setFilters] = useState({
    search: '',
    adminStatus: undefined,
    page: 1,
    pageSize: 20,
  })
  const [statusModal, setStatusModal] = useState(null)
  const [bulkStatusModal, setBulkStatusModal] = useState(false)
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [bulkLoading, setBulkLoading] = useState(false)

  // Construir filtros solo con valores definidos
  const cleanFilters = {}
  if (filters.search?.trim()) {
    cleanFilters.search = filters.search.trim()
  }
  if (filters.adminStatus) {
    cleanFilters.adminStatus = filters.adminStatus
  }
  if (filters.page) {
    cleanFilters.page = filters.page
  }
  if (filters.pageSize) {
    cleanFilters.pageSize = filters.pageSize
  }

  const [result, reexecuteQuery] = useQuery({
    query: GET_COMPANIES,
    variables: { filter: cleanFilters },
    requestPolicy: 'network-only', // Desactivar caché para esta query
  })

  const [, updateStatus] = useMutation(UPDATE_COMPANY_STATUS)

  const { data, fetching, error } = result
  const companies = data?.companies?.items || []
  const pagination = data?.companies || { totalCount: 0, page: 1, totalPages: 1 }

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === '' ? undefined : value,
      page: 1,
    }))
    setSelectedIds(new Set())
  }

  const handleStatusChange = async (companyId, newStatus) => {
    await updateStatus({
      input: { companyId, newStatus },
    })
    setStatusModal(null)
    reexecuteQuery({ requestPolicy: 'network-only' })
  }

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(new Set(companies.map((c) => c._id)))
    } else {
      setSelectedIds(new Set())
    }
  }

  const handleSelectOne = (id) => {
    const newSelected = new Set(selectedIds)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedIds(newSelected)
  }

  const handleBulkStatusChange = async (_, newStatus) => {
    if (selectedIds.size === 0) {
      alert('No hay empresas seleccionadas')
      return
    }
    
    setBulkLoading(true)
    try {
      const promises = Array.from(selectedIds).map((companyId) =>
        updateStatus({ input: { companyId, newStatus } })
      )
      await Promise.all(promises)
      setBulkStatusModal(false)
      setSelectedIds(new Set())
      reexecuteQuery({ requestPolicy: 'network-only' })
    } catch (err) {
      alert('Error al aplicar cambios masivos')
    }
    setBulkLoading(false)
  }

  const formatCalendarDate = (dateStr) => {
    if (!dateStr) return null
    const raw = typeof dateStr === 'string' ? dateStr : new Date(dateStr).toISOString()
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (!match) return null
    const [, y, m, d] = match
    return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString('es-CL')
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return formatCalendarDate(dateStr) || '-'
  }

  const isAllSelected = companies.length > 0 && selectedIds.size === companies.length

  if (error) {
    return (
      <div className="content-card">
        <div className="card-body">
          <div className="empty-state">
            <p>Error al cargar empresas: {error.message}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      {!embedded ? (
        <div className="page-header">
          <h1 className="page-title">Empresas</h1>
          <p className="page-subtitle">Gestión de empresas registradas</p>
        </div>
      ) : null}

      <div className="content-card">
        <div className="card-header">
          <h3 className="card-title">Lista de Empresas</h3>
          {selectedIds.size > 0 && (
            <div className="bulk-actions">
              <span className="bulk-count">{selectedIds.size} seleccionadas</span>
              <button
                className="btn btn-warning btn-sm"
                onClick={() => setBulkStatusModal(true)}
                disabled={bulkLoading}
              >
                Cambiar Estado
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setSelectedIds(new Set())}
              >
                Cancelar
              </button>
            </div>
          )}
        </div>
        <div className="card-body">
          {/* Filtros mejorados */}
          <div className="filters-container">
            <div className="filter-group">
              <label className="filter-label">BUSCAR</label>
              <input
                type="text"
                className="filter-input"
                placeholder="Nombre, RUT, alias o correlativo..."
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">ESTADO</label>
              <select
                className="filter-select"
                value={filters.adminStatus || ''}
                onChange={(e) => handleFilterChange('adminStatus', e.target.value)}
              >
                <option value="">Todos</option>
                <option value="NORMAL">Activa</option>
                <option value="ROJO">Pago Pendiente</option>
                <option value="BLOQUEADO">Bloqueada</option>
              </select>
            </div>
          </div>

          {fetching ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
            </div>
          ) : companies.length === 0 ? (
            <div className="empty-state">
              <p>No se encontraron empresas</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                      title="Seleccionar todas"
                    />
                  </th>
                  <th>EMPRESA</th>
                  <th>CORRELATIVO</th>
                  <th>RUT</th>
                  <th>GRUPO</th>
                  <th>SERVICIO</th>
                  <th>USUARIOS</th>
                  <th>ESTADO</th>
                  <th>INGRESO</th>
                  <th>SALIDA</th>
                  <th>ACCIONES</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => (
                  <tr 
                    key={company._id}
                    className={`clickable-row ${selectedIds.has(company._id) ? 'selected-row' : ''}`}
                    onClick={() => onSelectCompany(company._id)}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(company._id)}
                        onChange={() => handleSelectOne(company._id)}
                      />
                    </td>
                    <td>
                      <div className="company-name">{company.name}</div>
                      {company.alias && (
                        <div className="company-alias">{company.alias}</div>
                      )}
                    </td>
                    <td>{company.correlative || '—'}</td>
                    <td>{company.rut || '-'}</td>
                    <td>{company.group?.groupName || '-'}</td>
                    <td>
                      <ServiceBadge plan={company.scfo_plan} />
                    </td>
                    <td className="text-center">{company.usersCount || 0}</td>
                    <td>
                      <StatusBadge status={company.adminStatus} />
                    </td>
                    <td>{company.entryDate ? formatDate(company.entryDate) : 's/i'}</td>
                    <td style={{ color: company.exitDate ? '#e74c3c' : '#999' }}>
                      {company.exitDate ? formatDate(company.exitDate) : '-'}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="action-buttons">
                        <button
                          className="btn btn-outline-warning btn-sm"
                          onClick={() => setStatusModal(company)}
                        >
                          Estado
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {pagination.totalPages > 1 && (
          <div className="pagination">
            <div className="pagination-info">
              Mostrando {companies.length} de {pagination.totalCount} empresas
            </div>
            <div className="pagination-controls">
              <button
                className="btn btn-secondary btn-sm"
                disabled={pagination.page <= 1}
                onClick={() => setFilters((prev) => ({ ...prev, page: prev.page - 1 }))}
              >
                ← Anterior
              </button>
              <span className="pagination-pages">
                Página {pagination.page} de {pagination.totalPages}
              </span>
              <button
                className="btn btn-secondary btn-sm"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => setFilters((prev) => ({ ...prev, page: prev.page + 1 }))}
              >
                Siguiente →
              </button>
            </div>
          </div>
        )}
      </div>

      {statusModal && (
        <StatusModal
          company={statusModal}
          onClose={() => setStatusModal(null)}
          onConfirm={handleStatusChange}
        />
      )}

      {bulkStatusModal && (
        <StatusModal
          company={null}
          onClose={() => setBulkStatusModal(false)}
          onConfirm={handleBulkStatusChange}
          isBulk={true}
          selectedCount={selectedIds.size}
        />
      )}
    </>
  )
}

export default CompaniesList
